import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { IonText, IonGrid, IonRow } from '@ionic/react'

import {
	ScreenStoryContainer as Container,
	SCREEN_SECTION_TYPE_TEXT,
	SCREEN_SECTION_TYPE_LINK_GROUP,
	SCREEN_SECTION_TYPE_MEDIA_GROUP,
	SCREEN_SECTION_TYPE_AUDIO,
	SCREEN_SECTION_TYPE_IMAGE,
	SCREEN_SECTION_TYPE_VIDEO,
	SCREEN_SECTION_TYPE_LOCATION,
	SCREEN_SECTION_TYPE_SOCIAL_GROUP,
	SCREEN_SECTION_TYPE_PRICE_GROUP,
	SCREEN_SECTION_TYPE_OPENING_TIME_GROUP,
	SCREEN_SECTION_TYPE_IMAGE_SLIDER,
	SCREEN_SECTION_TYPE_MENU,
	SCREEN_SECTION_TYPE_QUIZ_QUESTION,
	SCREEN_SECTION_TYPE_QUIZ_SCORE,
	SCREEN_SECTION_TYPE_SINGLE_MEDIA,
	SCREEN_SECTION_TYPE_BADGE_GROUP,
	SCREEN_SECTION_HEADER_LAYOUT_IMAGE_AND_TITLE,
	ScreenSection,
	ScreenSectionItem,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	COLLECTION_ITEM_SUBTYPE_STORY,
	RootStoreState,
} from 'mytours-read-common'
import SectionLinkGroupComponent from './parts/SectionLinkGroup'
import SectionLocationComponent from './parts/SectionLocation'
import SectionSocialGroupComponent from './parts/SectionSocialGroup'
import SectionListGroupComponent from './parts/SectionListGroup'
import SectionImageSliderComponent from './parts/SectionImageSlider'
import SectionMenuComponent from './parts/SectionMenu'
import AudioPlayerBarComponent from '../../audio/components/AudioPlayerBar'
import SectionQuizQuestion from './parts/SectionQuizQuestion'
import SectionMediaGroup from './parts/SectionMediaGroup'
import SectionSingleMedia from './parts/SectionSingleMedia'
import SectionBadgeGroup from './parts/SectionBadgeGroup'
import Header from '../../common/components/header'
import Page from '../../common/components/Page'
import { OGMeta } from '../types'

import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../../common_lib/utils'
import { GetCollectionItemsIndexResponseData, GetScreensShowResponse } from 'mytours-read-api-typescript'
import { AVAILABLE_SCREEN_SECTIONS, renderScreenRedirect } from '../functions'
import StoryContentPlaceholder from '../../common/components/placeholders/StoryContent'
import { loadQuiz } from 'mobile_web/modules/quiz/queries'
import { ionHistory } from 'mobile_web/modules/navigation'
import ImagePlaceholder from 'mobile_web/modules/common/components/placeholders/Image'
import { getPlaceholderTypeByPathname } from 'mobile_web/modules/common/components/placeholders/functions'

import strings from '../../../strings'
import classNames from 'classnames'

import './styles/screenStory.less'
import usePrevious from 'common_lib/hooks/use-previous'
import ErrorMessageHandler from 'mobile_web/modules/common/components/error-message-handler'
import SectionQuizScore from './parts/SectionQuizScore'
import { getDirMode } from 'mobile_web/modules/platform'

import { useLiveQuery } from 'dexie-react-hooks'
import db from 'mobile_web/modules/db'
import SectionTextComponent from './parts/SectionText'

interface OwnProps {
	isPageContent?: boolean
	closeModal?: () => void
	isInsideModal?: boolean
}

type Props = Container.Props & Container.Actions & OwnProps

const ScreenStory: React.FC<Props> = props => {
	const [stateLoading, setStateLoading] = useState<boolean>(true)
	const [ogMetaTags, setOgMetaTags] = useState<OGMeta[]>()

	const prevPropsItemId: number = usePrevious(props.itemId)
	const prevPropsItemVersion: number = usePrevious(props.itemVersion)
	const currentLanguage = useSelector((state: RootStoreState) => state.settings.selectedLanguage)
	const manifestQuizzes = useSelector((state: RootStoreState) => state.settings.appManifest.quizzes)

	const earnedBadges = useLiveQuery(() => db.recentlyEarnedBadges.toArray())
	const trigger = useLiveQuery(
		() => db.triggers.where({ triggerable_id: props?.itemId, trigger_type: 'screen' }).first(),
		[props.itemId],
	)

	useEffect(() => {
		loadItem()
	}, [])

	useEffect(() => {
		const { screen, itemId, itemVersion } = props
		if (!screen || prevPropsItemId !== itemId || prevPropsItemVersion !== itemVersion) {
			loadItem()
		} else if (screen?.sections && ogMetaTags === undefined) {
			setupOGMetaTags(screen.sections)
			setStateLoading(false)
		}
	}, [props.screen, props.itemId, props.itemVersion])

	useEffect(() => {
		if (trigger && earnedBadges) {
			const earned = !!earnedBadges.find(b => b.badge_id === trigger.badge_id)
			if (!earned) {
				props.toggleBadgeModal(true, trigger.badge_id, true, true)
			}
		}
	}, [props.itemId, trigger, earnedBadges])

	useEffect(() => {
		const { screen, itemVersion } = props
		if (screen && manifestQuizzes && currentLanguage) {
			const quizzes = screen?.sections?.filter(section => section.section_type === SCREEN_SECTION_TYPE_QUIZ_QUESTION)
			const quizIds = quizzes?.map(quiz => quiz.quiz_id)
			const uniqueIds = quizIds?.filter((elem, index) => quizIds.indexOf(elem) === index)

			uniqueIds?.map(quizId => {
				;(async () => {
					const quizVersion = manifestQuizzes?.find(quiz => quiz.id === quizId).version
					await loadQuiz(quizId, quizVersion, currentLanguage.code, prevPropsItemVersion !== itemVersion)
				})()
			})
		}
	}, [props.screen, props.itemVersion, currentLanguage, manifestQuizzes])

	/*
	 * Creating array of meta fields. Needs for SEO friendly
	 */
	const setupOGMetaTags = (sections: ScreenSection[]) => {
		const ogMetaTags = []

		sections.forEach(section => {
			if (section.section_type === SCREEN_SECTION_TYPE_MEDIA_GROUP) {
				section.section_items.forEach((sectionItem: ScreenSectionItem) => {
					if (sectionItem.file) {
						const { original_url: originalUrl, content_type: contentType } = sectionItem.file
						switch (sectionItem.media_type) {
							case SCREEN_SECTION_TYPE_AUDIO:
								ogMetaTags.push(
									{ property: 'og:audio', content: originalUrl },
									{ property: 'og:audio:type', content: contentType },
								)
								break
							case SCREEN_SECTION_TYPE_VIDEO: {
								const { width, height } = sectionItem.file
								ogMetaTags.push(
									{ property: 'og:video', content: originalUrl },
									{ property: 'og:video:type', content: contentType },
									{ property: 'og:video:width', content: width },
									{ property: 'og:video:height', content: height },
								)
								break
							}
							case SCREEN_SECTION_TYPE_IMAGE: {
								const { width, height } = sectionItem.file
								ogMetaTags.push(
									{ property: 'og:image', content: originalUrl },
									{ property: 'og:image:type', content: contentType },
									{ property: 'og:image:width', content: width },
									{ property: 'og:image:height', content: height },
								)

								if (sectionItem.description) {
									ogMetaTags.push({
										property: 'og:image:alt',
										content: sectionItem.description,
									})
								}
								break
							}
						}
					}
				})
			}
		})

		setOgMetaTags(ogMetaTags)
	}

	const coverImageUrl = useMemo(() => {
		const { screen: { cover_image: coverImage } = {} } = props
		return coverImage?.file
			? generateImageUrl(coverImage.file, {
				imageSizePreset: ImageSizePreset.Row,
			})
			: undefined
	}, [props.screen])
	const coverImageSrcSet = useMemo(() => {
		const { screen: { cover_image: coverImage } = {} } = props
		return coverImage?.file
			? generateImageSrcSet(coverImage.file, {
				imageSizePreset: ImageSizePreset.Row,
			  })
			: undefined
	}, [props.screen])

	const loadItem = () => {
		const { itemId, itemVersion, language, accountId } = props

		if (!itemVersion) return

		props.loadItem(itemId, itemVersion, language, accountId)
	}

	const renderSections = (): JSX.Element[] => {
		const { screen } = props
		if (!screen?.sections) {
			return null
		}

		return screen.sections.map((section: ScreenSection, index: number) => {
			// return nothing when a user hides section in the builder
			if (section.hide) return null

			// return nothing when a section is not implemented
			if (!AVAILABLE_SCREEN_SECTIONS.includes(section.section_type)) return null

			// return nothing when there are section items parameter and it is empty
			if (Array.isArray(section.section_items) && section.section_items?.length === 0) return

			return (
				<IonRow
					id={`${section.section_type}-${section.id}`}
					data-item-id={section.id}
					data-item-type="screen_section"
					key={index}
					className="block"
					data-cy={`story-screen-section-${section.id}`}
				>
					{renderItem(section)}
				</IonRow>
			)
		})
	}

	const renderItem = (section: ScreenSection): JSX.Element => {
		const { screen = {}, collection, closeModal } = props

		switch (section.section_type) {
			case SCREEN_SECTION_TYPE_TEXT:
				return <SectionTextComponent key={section.id} section={section} isInsideModal={isInsideModal} />
			case SCREEN_SECTION_TYPE_LINK_GROUP:
				return (
					<SectionLinkGroupComponent
						section={section}
						collectionId={collection?.id}
						screen={screen}
						closeModal={closeModal}
						isInsideModal={isInsideModal}
					/>
				)
			case SCREEN_SECTION_TYPE_LOCATION:
				return <SectionLocationComponent section={section} collectionItemId={collection?.id} screen={screen} />
			case SCREEN_SECTION_TYPE_MEDIA_GROUP: {
				// we do not want to set margin on content here in case we are gonna display the Carousel which needs to reach sides of screen
				const { loading: propsLoading } = props

				if (propsLoading || stateLoading) {
					return null
				}

				return <SectionMediaGroup collection={collection} screen={screen} key={section.id} section={section} />
			}
			case SCREEN_SECTION_TYPE_SOCIAL_GROUP:
				return <SectionSocialGroupComponent key={section.id} section={section} />
			case SCREEN_SECTION_TYPE_PRICE_GROUP:
				return <SectionListGroupComponent key={section.id} section={section} type="price" />
			case SCREEN_SECTION_TYPE_OPENING_TIME_GROUP:
				return <SectionListGroupComponent key={section.id} section={section} type="opening_hours" />
			case SCREEN_SECTION_TYPE_IMAGE_SLIDER:
				return <SectionImageSliderComponent key={section.id} section={section} />
			case SCREEN_SECTION_TYPE_MENU:
				return <SectionMenuComponent key={section.id} section={section} />
			case SCREEN_SECTION_TYPE_QUIZ_QUESTION:
				return <SectionQuizQuestion key={section.id} section={section} language={currentLanguage.code} />
			case SCREEN_SECTION_TYPE_QUIZ_SCORE:
				return <SectionQuizScore key={section.id} section={section} />
			case SCREEN_SECTION_TYPE_SINGLE_MEDIA:
				return (
					<SectionSingleMedia
						key={section.id}
						section={section}
						collection={collection}
						screen={screen}
						isInsideModal={isInsideModal}
					/>
				)
			case SCREEN_SECTION_TYPE_BADGE_GROUP:
				return <SectionBadgeGroup key={section.id} section={section} />
			default:
				return undefined
		}
	}

	const { error, screen = {}, isPageContent, onBackAction, loading: propsLoading, isInsideModal } = props
	const { title, cover_image: coverImage, header_layout: headerLayout, screen_type: screenType } = screen
	const { pathname } = ionHistory.location

	const loading = propsLoading || stateLoading

	const isImageWithTitleHeader = headerLayout === SCREEN_SECTION_HEADER_LAYOUT_IMAGE_AND_TITLE

	const fullHeight = propsLoading || loading || error || (!loading && !error && !screen?.sections?.length)
	const placeholderStoryType = getPlaceholderTypeByPathname(screenType || pathname)
	const isPageWithoutTabs =
		pathname.includes(COLLECTION_ITEM_SUBTYPE_STORY) || pathname.includes(COLLECTION_ITEM_SUBTYPE_PANORAMA)

	const screenStoryGrid = (isFullPage: boolean, isImageWithTitleHeader: boolean) => (
		<>
			{ogMetaTags && <Helmet meta={ogMetaTags} />}
			{!loading && (!isPageContent || headerLayout !== 'none') && (
				<Header
					coverImage={
						coverImageUrl
							? {
									imageData: coverImage,
									imageUrl: coverImageUrl,
									imageSrcSet: coverImageSrcSet,
							  }
							: undefined
					}
					title={isImageWithTitleHeader ? title || '' : ''}
					type={headerLayout === 'none' ? 'short' : headerLayout}
					hideBackButton={!isFullPage || isInsideModal}
					onBackButton={onBackAction}
					showBackButton={Boolean(onBackAction)}
					collectionItem={null}
					itemType={GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen}
					itemId={screen.id}
					collection={null}
					screen={screen}
					hideFavouriteButton={false}
				/>
			)}
			<IonGrid
				className={classNames('screen-story-grid', {
					'full-height': fullHeight,
				})}
				dir={getDirMode()}
				{...(isPageContent ? {
					"data-item-id": String(props.itemId),
					"data-item-type": "screen",
					"data-item-subtype": "story"	
				} : {})}
			>
				<IonRow className="main-container-row">
					<ErrorMessageHandler error={error} />

					{!error && (
						<>
							{title && <h2 className="hidden">{title}</h2>}
							{screen?.sections?.length
								? renderSections()
								: !loading && <IonText className="center-message">{strings.no_content_available}</IonText>}
						</>
					)}
				</IonRow>
			</IonGrid>
		</>
	)

	const redirect = renderScreenRedirect(GetScreensShowResponse.ScreenTypeEnum.Story, screenType)
	if (redirect) {
		return redirect
	}

	if (isPageContent) {
		return screenStoryGrid(false, isImageWithTitleHeader)
	} else {
		return (
			<>
				<Helmet
					title={title}
					meta={[
						{ name: 'description', content: title }, // TODO: add description field to story
						{ property: 'og:type', content: 'website' }, // TODO: check type
						{ property: 'og:title', content: title },
						{ property: 'og:description', content: title }, // TODO: add description field to story
						{ property: 'og:url', content: window.location.href },
					]}
				/>
				<Page
					contentProps={{
						scrollEvents: isImageWithTitleHeader,
						style: props.screen?.background_image
							? {
									'--content-background-image': `url(${generateImageUrl(props.screen.background_image?.file, {
										imageSizePreset: ImageSizePreset.Fullscreen,
										focalPoint: props.screen.background_image.file?.focal_point
									})})`,
							  }
							: undefined,
					}}
					footer={{
						component: !isInsideModal ? <AudioPlayerBarComponent bottomOffset={isPageWithoutTabs ? 0 : -1} /> : null,
					}}
					dataItemId={String(props.itemId)}
					dataItemType="screen"
					dataItemSubtype="story"
					dataCy="page-screen-story"
				>
					{loading ? (
						[COLLECTION_ITEM_SUBTYPE_PANORAMA, COLLECTION_ITEM_SUBTYPE_WEB].includes(placeholderStoryType) ? (
							<ImagePlaceholder style={{ height: '100%' }} />
						) : (
							<StoryContentPlaceholder style={{ padding: '0 24px' }} />
						)
					) : (
						screenStoryGrid(true, isImageWithTitleHeader)
					)}
				</Page>
			</>
		)
	}
}

const ScreenStoryComponent = Container.default(ScreenStory)
export default ScreenStoryComponent as any
